import { RouteRecordRaw } from 'vue-router';

import usePaymentEntitlement from '@/composables/usePaymentEntitlement';

declare module 'vue-router' {
  interface RouteMeta {
    title: string;
    requiresAuth: boolean;
    contexts?: Array<ContextType>;
  }
}

export enum RouteNames {
  AccountInfo = 'accountInformation',
  Cards = 'cards',
  CardsAdmin = 'cardsAdmin',
  Dashboard = 'dashboard',
  Legal = 'legal',
  Login = 'login',
  LoginMoreInfo = 'more-info',
  PersonalDetails = 'personal-details',
  PartnershipDetails = 'partnership-details',
  Settings = 'settings',
  Suggestions = 'suggestions',
  Documents = 'documents',
  Transactions = 'transactions',
  SmeSubscription = 'sme-subscription',
  ChangeSmeSubscription = 'sme-subscription-change',
  Registrations = 'registrations',
  AccountConfirmations = 'account-confirmations',
  ViaApp = 'via-app',
  RegisterNonRegistered = 'register-non-registered',
  UpdateInternalRegistry = 'update-internal-registry',
  ActivateEbill = 'activate-ebill',
  EbillSettings = 'ebill-settings',
  EbillBillers = 'ebill-billers',
  ComplianceDeclaration = 'compliance-declaration',
  PaymentOverview = 'payment-overview',
  AccountRequest = 'account-request',
  NewPayment = 'new-payment',
  NewIbanPayment = 'new-iban-payment',
  NewInternalPayment = 'new-internal-payment',
  InputTesting = 'input-testing',
  ButtonTesting = 'button-testing',
  BadgeTesting = 'badge-testing',
  AlertTesting = 'alert-testing',
  MaskedInputTesting = 'masked-input-testing',
  YapPageTesting = 'yap-page-testing',
  ColorTesting = 'color-testing',
  YapFileResourceUploader = 'yap-file-resource-testing',
  ComponentTesting = 'component-testing',
  BulkOrders = 'bulk-orders',
  NoActiveRoles = 'no-active-roles',
  CashAccountCreate = 'cash-account-create',
  AccountOpeningConditions = 'account-opening-conditions',
  TeamSpace = 'team-space'
}

export enum ContextType {
  Private = 'private',
  Partnership = 'partnership',
  Registered = 'registered'
}

export const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: RouteNames.Login,
    component: () => import('@/views/Login.vue'),
    meta: { title: 'Yapeal - Login', requiresAuth: false }
  },
  {
    path: '/login/more-info',
    name: RouteNames.LoginMoreInfo,
    component: () => import('@/views/LoginMoreInfo.vue'),
    meta: { title: 'Yapeal - More Info', requiresAuth: false }
  },
  {
    path: '/account-request',
    name: RouteNames.AccountRequest,
    component: () => import('@/views/AccountRequest.vue'),
    meta: { title: 'Yapeal - Account Request', requiresAuth: false }
  },
  {
    path: '/',
    name: RouteNames.Dashboard,
    component: () => import('@/views/Dashboard.vue'),
    meta: { title: 'Yapeal - Dashboard', requiresAuth: true }
  },
  {
    path: '/transactions/:id?',
    name: RouteNames.Transactions,
    component: () => import('@/views/Transactions.vue'),
    meta: { title: 'Yapeal - Transactions', requiresAuth: true }
  },
  {
    path: '/payments',
    name: RouteNames.NewPayment,
    component: () => import('@/views/payments/NewPayment.vue'),
    meta: { title: 'Yapeal - New Payment', requiresAuth: true },
    beforeEnter(to, from, next) {
      const { canCreatePaymentOnSelectedCashAccount } = usePaymentEntitlement();
      if (canCreatePaymentOnSelectedCashAccount()) {
        next();
      } else {
        next({
          name: RouteNames.Dashboard
        });
      }
    }
  },
  {
    path: '/payments/new/:id?',
    name: RouteNames.NewIbanPayment,
    component: () => import('@/views/payments/NewIbanPayment.vue'),
    meta: { title: 'Yapeal - New IBAN Payment', requiresAuth: true }
  },
  {
    path: '/payments/newInternal/:id?',
    name: RouteNames.NewInternalPayment,
    component: () => import('@/views/payments/NewInternalPayment.vue'),
    meta: { title: 'Yapeal - New Internal Payment', requiresAuth: true }
  },
  {
    path: '/cards/:id?',
    name: 'cards',
    component: () => import('@/views/Cards.vue'),
    meta: { title: 'Yapeal - Cards', requiresAuth: true }
  },
  {
    path: '/cards-admin/:id?/:cardId?',
    name: RouteNames.CardsAdmin,
    component: () => import('@/views/CardsAdmin.vue'),
    meta: { title: 'Yapeal - Card Admin', requiresAuth: true, contexts: [ContextType.Partnership] }
  },
  {
    path: '/legal/:id?',
    name: RouteNames.Legal,
    component: () => import('@/views/Legal.vue'),
    meta: { title: 'Yapeal - Legal', requiresAuth: true }
  },
  {
    path: '/settings/:id?',
    name: RouteNames.Settings,
    component: () => import('@/views/Settings.vue'),
    meta: { title: 'Yapeal - Settings', requiresAuth: true }
  },
  {
    path: '/suggestions/:id?',
    name: RouteNames.Suggestions,
    component: () => import('@/views/YapSuggestions.vue'),
    meta: { title: 'Yapeal - Suggestions', requiresAuth: true }
  },
  {
    path: '/documents/:id?',
    name: RouteNames.Documents,
    component: () => import('@/views/YapDocuments.vue'),
    meta: { title: 'Yapeal - Settings', requiresAuth: true }
  },
  {
    path: '/account-information',
    name: RouteNames.AccountInfo,
    component: () => import('@/views/AccountInformation.vue'),
    meta: {
      title: 'Yapeal - Account Info',
      requiresAuth: true,
      contexts: [ContextType.Private, ContextType.Partnership]
    }
  },
  {
    path: '/cash-account-create',
    name: RouteNames.CashAccountCreate,
    component: () => import('@/views/CashAccountCreate.vue'),
    meta: { title: 'Yapeal - Cash Account Create', requiresAuth: true, contexts: [ContextType.Partnership] }
  },
  {
    path: '/account-opening-conditions',
    name: RouteNames.AccountOpeningConditions,
    component: () => import('@/views/AccountOpeningConditions.vue'),
    meta: { title: 'Yapeal - Conditions', requiresAuth: true, contexts: [ContextType.Partnership] }
  },
  {
    path: '/personal-details/:id?',
    name: RouteNames.PersonalDetails,
    component: () => import('@/views/PersonalDetails.vue'),
    meta: { title: 'Yapeal - Personal Details', requiresAuth: true, contexts: [ContextType.Private] }
  },
  {
    path: '/partnership-details/:id?',
    name: RouteNames.PartnershipDetails,
    component: () => import('@/views/PartnershipDetails.vue'),
    meta: { title: 'Yapeal - Partnership Details', requiresAuth: true, contexts: [ContextType.Partnership] }
  },
  {
    path: '/sme-subscription',
    name: RouteNames.SmeSubscription,
    component: () => import('@/views/SmeSubscription.vue'),
    meta: { title: 'Yapeal - SME Subscription', requiresAuth: true, contexts: [ContextType.Partnership] }
  },
  {
    path: '/sme-subscription-change',
    name: RouteNames.ChangeSmeSubscription,
    component: () => import('@/views/SmeSubscriptionChange.vue'),
    meta: { title: 'Yapeal - Change SME Subscription', requiresAuth: true, contexts: [ContextType.Partnership] }
  },
  {
    path: '/r3g15t3r/registrations/:id?',
    name: RouteNames.Registrations,
    component: () => import('@/views/PendingRegistrations.vue'),
    meta: { title: 'Yapeal - Registrations', requiresAuth: true, contexts: [ContextType.Private] }
  },
  {
    path: '/account-confirmations/:id?',
    name: RouteNames.AccountConfirmations,
    component: () => import('@/views/AccountConfirmations.vue'),
    meta: { title: 'Yapeal - Account Confirmations', requiresAuth: true, contexts: [ContextType.Private] }
  },
  {
    path: '/via-app',
    name: RouteNames.ViaApp,
    component: () => import('@/views/ViaApp.vue'),
    meta: { title: 'Yapeal - Via App', requiresAuth: true, contexts: [ContextType.Private] }
  },
  {
    path: '/r3g15t3r/:id?/:step?',
    name: RouteNames.RegisterNonRegistered,
    component: () => import('@/views/RegisterNonRegistered.vue'),
    meta: { title: 'Yapeal - Register', requiresAuth: true, contexts: [ContextType.Private] }
  },
  {
    path: '/partnership-details/update-partnership-form/:id?/:step?',
    name: RouteNames.UpdateInternalRegistry,
    component: () => import('@/views/RegisterNonRegistered.vue'),
    meta: { title: 'Yapeal - Update', requiresAuth: true, contexts: [ContextType.Partnership] }
  },
  {
    path: '/ebill/activate',
    name: RouteNames.ActivateEbill,
    component: () => import('@/views/ActivateEbill.vue'),
    meta: {
      title: 'Yapeal - Activate eBill',
      requiresAuth: true,
      contexts: [ContextType.Private, ContextType.Partnership]
    }
  },
  {
    path: '/ebill/settings/:id?',
    name: RouteNames.EbillSettings,
    component: () => import('@/views/EbillSettings.vue'),
    meta: {
      title: 'Yapeal - eBill settings',
      requiresAuth: true,
      contexts: [ContextType.Private, ContextType.Partnership]
    }
  },
  {
    path: '/ebill/billers/:id?',
    name: RouteNames.EbillBillers,
    component: () => import('@/views/EbillBillers.vue'),
    meta: {
      title: 'Yapeal - eBill billers',
      requiresAuth: true,
      contexts: [ContextType.Private, ContextType.Partnership]
    }
  },
  {
    path: '/compliance-declaration/:id?',
    name: RouteNames.ComplianceDeclaration,
    component: () => import('@/views/ComplianceDeclaration.vue'),
    meta: {
      title: 'Yapeal - Compliance Declaration',
      requiresAuth: true,
      contexts: [ContextType.Partnership]
    }
  },
  {
    path: '/team-space/:view?/:id?',
    name: RouteNames.TeamSpace,
    component: () => import('@/views/TeamSpace.vue'),
    meta: {
      title: 'Yapeal - Team Space',
      requiresAuth: true,
      contexts: [ContextType.Partnership]
    }
  },
  {
    path: '/payment/overview/:id?',
    name: RouteNames.PaymentOverview,
    component: () => import('@/views/PaymentOverview.vue'),
    meta: {
      title: 'Yapeal - eBill overview',
      requiresAuth: true,
      contexts: [ContextType.Private, ContextType.Partnership]
    }
  },
  {
    path: '/no-active-roles',
    name: RouteNames.NoActiveRoles,
    component: () => import('@/views/NoActiveRoles.vue'),
    meta: {
      title: 'Yapeal - Roles',
      requiresAuth: true,
      contexts: [ContextType.Partnership]
    }
  },
  {
    path: '/debug/input',
    name: RouteNames.InputTesting,
    component: () => import('@/views/debug/InputTest.vue'),
    meta: { title: 'Yapeal - Input Test', requiresAuth: true }
  },
  {
    path: '/debug/button',
    name: RouteNames.ButtonTesting,
    component: () => import('@/views/debug/ButtonTest.vue'),
    meta: { title: 'Yapeal - Button Test', requiresAuth: true }
  },
  {
    path: '/debug/badge',
    name: RouteNames.BadgeTesting,
    component: () => import('@/views/debug/BadgeTest.vue'),
    meta: { title: 'Yapeal - Badge Test', requiresAuth: true }
  },
  {
    path: '/debug/alerts',
    name: RouteNames.AlertTesting,
    component: () => import('@/views/debug/AlertTest.vue'),
    meta: { title: 'Yapeal - Alert Test', requiresAuth: true }
  },
  {
    path: '/debug/masked-input',
    name: RouteNames.MaskedInputTesting,
    component: () => import('@/views/debug/MaskedInputTest.vue'),
    meta: { title: 'Yapeal - Masked Input Test', requiresAuth: true }
  },
  {
    path: '/debug/yap-page',
    name: RouteNames.YapPageTesting,
    component: () => import('@/views/debug/YapPageTest.vue'),
    meta: { title: 'Yapeal - YapPage Test', requiresAuth: true }
  },
  {
    path: '/debug/color-test',
    name: RouteNames.ColorTesting,
    component: () => import('@/views/debug/ColorTest.vue'),
    meta: { title: 'Yapeal - Color Test', requiresAuth: true }
  },
  {
    path: '/debug/components',
    name: RouteNames.ComponentTesting,
    component: () => import('@/views/debug/YapComponents.vue'),
    meta: { title: 'Yapeal - Components Test', requiresAuth: true }
  },
  {
    path: '/debug/file-resource-uploader',
    name: RouteNames.YapFileResourceUploader,
    component: () => import('@/views/debug/YapFileResource.vue'),
    meta: { title: 'Yapeal - File Resource Uploader Test', requiresAuth: true }
  }
];
